import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './styles/theme';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

// Initialize Eruda for mobile debugging (works on iPhone Safari)
// Enable by adding ?debug=true to URL only (disabled in development by default)
if (new URLSearchParams(window.location.search).get('debug') === 'true') {
  import('eruda').then((eruda) => {
    eruda.default.init();
    console.log('🔧 Eruda mobile console enabled! Tap the floating button to open console.');
  }).catch((err) => {
    console.warn('Eruda not available:', err);
  });
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <App />
  </ThemeProvider>
);

const BUILD_KEY_STORAGE = 'dreamey_build_key';
const BUILD_RELOAD_GUARD = 'dreamey_build_reload_guard';

async function clearAppCachesForRefresh() {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch (err) {
    console.warn('[App Update] Cache cleanup skipped:', err?.message || err);
  }
}

async function refreshOnNewBuild() {
  try {
    const manifestUrl = `${process.env.PUBLIC_URL || ''}/asset-manifest.json?t=${Date.now()}`;
    const res = await fetch(manifestUrl, { cache: 'no-store' });
    if (!res.ok) return;
    const manifest = await res.json();
    const buildKey =
      manifest?.files?.['main.js'] ||
      (Array.isArray(manifest?.entrypoints)
        ? manifest.entrypoints.find((p) => typeof p === 'string' && p.endsWith('.js'))
        : null);
    if (!buildKey) return;

    const prev = localStorage.getItem(BUILD_KEY_STORAGE);
    if (!prev) {
      localStorage.setItem(BUILD_KEY_STORAGE, buildKey);
      return;
    }

    if (prev !== buildKey) {
      const guard = sessionStorage.getItem(BUILD_RELOAD_GUARD);
      localStorage.setItem(BUILD_KEY_STORAGE, buildKey);
      if (guard === buildKey) return;
      sessionStorage.setItem(BUILD_RELOAD_GUARD, buildKey);
      await clearAppCachesForRefresh();
      window.location.reload();
    }
  } catch (err) {
    console.warn('[App Update] Manifest check failed:', err?.message || err);
  }
}

// Always disable service worker cache so users get latest portal version automatically.
serviceWorkerRegistration.unregister();

if (process.env.NODE_ENV === 'production') {
  refreshOnNewBuild();
}
